import axios from 'axios';

import assetProcessingActivitiesApi from 'config/api/assetProcessingActivities/assetProcessingActivities';
import processingActivitiesApi from 'config/api/processingActivities/processingActivities';
import type { AssetRopaReviewListParams } from 'config/api/ropaReviews/ropaReviews.types';
import createUrlWithParams from 'helpers/createUrlWithParams';

import {
  parseOwnerListResponse,
  parseOwnerMineDetailForFE,
  parseOwnerMineMetricsResponseForFE,
  parseOwnerWorkspaceResponse,
} from './assetRopa.parsers';
import type { AssetProcessingActivitySubmitInput, AssetProcessingActivityUpdateInput, AssetRopaApi } from './assetRopa.types';
import { getLinkedProcessingActivityIds } from './assetRopaWorkspace.helpers';
import type { AvailableProcessingActivity, OwnerWorkspaceAsset } from './assetRopaWorkspace.types';

const buildMineEndpoint = (organizationId: number) => `api/v1/organizations/${organizationId}/asset-ropa/mine/`;

const buildMineMetricsEndpoint = (organizationId: number) => `api/v1/organizations/${organizationId}/asset-ropa/mine/metrics/`;

const buildMineDetailEndpoint = (organizationId: number, assetRopaId: number) =>
  `api/v1/organizations/${organizationId}/asset-ropa/mine/${assetRopaId}/`;

const buildAssetRopaEndpoint = (organizationId: number, assetRopaId: number) =>
  `api/v1/organizations/${organizationId}/asset-ropa/${assetRopaId}/`;

export const assetRopaApi: AssetRopaApi = {
  getMine: async (organizationId, params?: AssetRopaReviewListParams) => {
    const { data } = await axios.get(createUrlWithParams(buildMineEndpoint(organizationId), params));
    return { data: parseOwnerListResponse(data) };
  },
  getMineMetrics: async organizationId => {
    const { data } = await axios.get(buildMineMetricsEndpoint(organizationId));
    return { data: parseOwnerMineMetricsResponseForFE(data) };
  },
  getMineDetail: async (organizationId, assetRopaId) => {
    const { data } = await axios.get(buildMineDetailEndpoint(organizationId, assetRopaId));
    return { data: parseOwnerMineDetailForFE(data) };
  },
  getOwnerWorkspace: async (organizationId, params?: AssetRopaReviewListParams) => {
    const { data } = await axios.get(createUrlWithParams(buildMineEndpoint(organizationId), params));
    return { data: parseOwnerWorkspaceResponse(data) };
  },
  getAvailableProcessingActivities: async (organizationId, assetRopaId) => {
    const [{ data: published }, { data: mine }] = await Promise.all([
      processingActivitiesApi.getAll(organizationId, { status: 'published', page_size: 200 }),
      assetRopaApi.getMine(organizationId),
    ]);

    const linkedIds = getLinkedProcessingActivityIds(mine.assets, assetRopaId);
    const available: AvailableProcessingActivity[] = published.results
      .filter(item => !linkedIds.includes(item.id))
      .map(item => ({
        id: item.id,
        name: item.name,
        processingRole: item.processingRole,
        status: item.status,
      }));

    return { data: available };
  },
  addProcessingActivity: async (organizationId, assetRopaId, payload) => {
    const { data } = await assetProcessingActivitiesApi.createForAssetRopa(organizationId, assetRopaId, payload);
    return { data: data as OwnerWorkspaceAsset };
  },
  softDeleteProcessingActivity: async (organizationId, assetRopaId, apaId, payload) => {
    const { data } = await assetProcessingActivitiesApi.softDelete(organizationId, assetRopaId, apaId, payload);
    return { data: data as OwnerWorkspaceAsset };
  },
  getProcessingActivity: async (organizationId, apaId) => assetProcessingActivitiesApi.getDetail(organizationId, apaId),
  updateProcessingActivity: async (organizationId, apaId, payload: AssetProcessingActivityUpdateInput) =>
    assetProcessingActivitiesApi.update(organizationId, apaId, payload),
  submitProcessingActivity: async (organizationId, apaId, payload: AssetProcessingActivitySubmitInput = {}) =>
    assetProcessingActivitiesApi.submit(organizationId, apaId, payload),
  confirmNoProcessingActivities: async (organizationId, assetRopaId) => {
    await axios.post(`${buildAssetRopaEndpoint(organizationId, assetRopaId)}confirm-no-processing-activities/`, {});
  },
};

export default assetRopaApi;
